import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, describeError } from "../../lib/api";
import { Spinner } from "../../components/ui";
import { ActivityFeed, ClubNumbers, GenrePills, MembersPreview } from "../../components/ClubHome";
import { CalendarIcon, ChevronIcon, PlusIcon, SettingsIcon } from "../../components/Icons";
import { countdown } from "../../components/MeetingCard";
import { useClub } from "./ClubLayout";
import { BoardThreadScreen, Noticeboard, useWide } from "../../components/Noticeboard";
import { useClubBooks, useMe, useMembers } from "../../lib/queries";
import { club } from "@pagemates/shared/copy";

const txt = club.tab;

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function ClubTab() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const me = useMe();
  const members = useMembers(clubId);
  const books = useClubBooks(clubId);
  const isAdmin = c.myRole !== "member";
  const isOwner = c.myRole === "owner";
  const { invite, post } = useSearch({ from: "/authed/clubs/$clubId/club" });
  const [inviting, setInviting] = useState(Boolean(invite)); // ?invite=1 (from "Invite someone" elsewhere) opens it
  const navigate = useNavigate();
  const wide = useWide();
  const setPost = (id: string | null) => void navigate({ to: "/clubs/$clubId/club", params: { clubId }, search: (s: { invite?: boolean; post?: string }) => ({ ...(s.invite ? { invite: true } : {}), ...(id ? { post: id } : {}) }), replace: wide });
  const meSummary = me.data ? { id: me.data.id, name: me.data.displayName ?? me.data.name, image: me.data.image } : null;

  // Phones: a noticeboard thread is its own screen.
  if (post && !wide && meSummary) return <BoardThreadScreen club={c} me={meSummary} postId={post} onBack={() => window.history.length > 1 ? window.history.back() : setPost(null)} />;

  return (
    <main className="page clubtab">
      <div className="stack">
        <section className="stack-sm who">
          <div className="row spread head-row">
            <h1 className="club-name">{c.name}</h1>
            {isAdmin && (
              <span className="row head-acts">
                <button type="button" className="btn primary sm" aria-expanded={inviting} onClick={() => setInviting((v) => !v)}><PlusIcon width={16} height={16} />{txt.inviteSomeone}</button>
                <Link to="/clubs/$clubId/club/settings" params={{ clubId }} className="btn sm set-btn"><SettingsIcon width={16} height={16} aria-hidden="true" />{txt.clubSettings}</Link>
              </span>
            )}
          </div>
          {c.description && <p className="tagline">{c.description}</p>}
          {c.rules ? <p className="rules">{c.rules}</p> : isAdmin && <p className="muted small">{txt.sayHowThisClub}<Link to="/clubs/$clubId/club/settings" params={{ clubId }}>settings</Link>{txt.howOftenYouMeet}</p>}
          {/* Genres with a label, after the club's own words: chosen ones read "Reads", inferred ones say where they came from. */}
          {c.shownGenres.length > 0 && (
            <div className="genre-line">
              <span className="gl-k">{c.genres.length > 0 ? txt.reads : txt.fromTheShelf}</span>
              <GenrePills genres={c.shownGenres} />
            </div>
          )}
        </section>

        {inviting && <Invites clubId={clubId} onClose={() => setInviting(false)} />}

        {c.bigClubNudge && <BigClubNudge clubId={clubId} />}

        {meSummary && <Noticeboard club={c} me={meSummary} openPost={post ?? null} setOpenPost={setPost} />}

        {c.meeting && (
          <Link to="/clubs/$clubId/meeting" params={{ clubId }} search={c.meeting.clubBookId ? { book: c.meeting.clubBookId } : {}} className="quiet link">
            <CalendarIcon /><div><b>{when(c.meeting.at)}</b><div className="d">{countdown(c.meeting.at)}{c.meeting.location ? ` · ${c.meeting.location}` : ""}{c.meeting.host ? txt.hosting(c.meeting.host.name) : ""}</div></div><ChevronIcon className="go" />
          </Link>
        )}

        <ClubNumbers clubId={clubId} />
        <ActivityFeed clubId={clubId} meId={me.data?.id} />
      </div>

      <div className="stack">
        {members.isPending ? <Spinner /> : members.data && <MembersPreview members={members.data} club={c} meId={me.data?.id} canManage={isAdmin} isOwner={isOwner} current={books.data?.current ?? null} />}
        {!isAdmin && <Link to="/clubs/$clubId/club/settings" params={{ clubId }} className="muted small membership-link">{txt.yourMembership}</Link>}
      </div>
    </main>
  );
}

function Invites({ clubId, onClose }: { clubId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const invites = useQuery({ queryKey: ["invites", clubId], queryFn: () => api.invites(clubId) });
  const [singleUse, setSingleUse] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);
  const [shown, setShown] = useState<string | null>(null);
  const create = useMutation({ mutationFn: () => api.createInvite(clubId, { singleUse }), onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", clubId] }) });
  const revoke = useMutation({ mutationFn: (id: string) => api.revokeInvite(clubId, id), onSuccess: () => qc.invalidateQueries({ queryKey: ["invites", clubId] }) });
  useEffect(() => { if (invites.data && invites.data.length === 0 && !create.isPending && !create.isSuccess) create.mutate(); }, [invites.data]); // eslint-disable-line react-hooks/exhaustive-deps

  async function copy(inv: { id: string; url: string }) {
    try { await navigator.clipboard.writeText(inv.url); setCopied(inv.id); setTimeout(() => setCopied(null), 1500); }
    catch { setShown(inv.id); } // clipboard refused: show the full link to select by hand
  }

  return (
    <section className="stack-sm">
      <div className="row spread"><h2>{txt.inviteLinks}</h2><button type="button" className="btn ghost sm" onClick={onClose}>{txt.done}</button></div>
      <p className="muted small">{txt.linksLast7Days}</p>
      {invites.data?.map((inv) => (
        <div key={inv.id} className="card stack-sm">
          <div className="row spread">
            <div className="grow">
              <div className="small" style={shown === inv.id ? { wordBreak: "break-all" } : { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <code className="inline">{inv.url.replace(/^https?:\/\//, "")}</code>
              </div>
              <div className="muted small">{txt.expires(new Date(inv.expiresAt).toLocaleDateString(undefined, { month: "short", day: "numeric" }), inv.singleUse ? txt.singleUse : txt.used(inv.uses))}</div>
            </div>
            <button className={`btn sm${copied === inv.id ? " copied" : ""}`} aria-live="polite" onClick={() => void copy(inv)}>{copied === inv.id ? <><span className="pop-check" aria-hidden="true">✓</span>{txt.copied}</> : txt.copy}</button>
            <button className="btn ghost sm" aria-label={txt.revokeInvite} onClick={() => revoke.mutate(inv.id)}>✕</button>
          </div>
        </div>
      ))}
      <div className="row" style={{ flexWrap: "wrap" }}>
        <button className="btn" disabled={create.isPending} onClick={() => create.mutate()}>{txt.newInviteLink}</button>
        <label className="row small muted"><input id="invite-single-use" type="checkbox" checked={singleUse} onChange={(e) => setSingleUse(e.target.checked)} />{txt.singleUse2}</label>
      </div>
      {(create.isError || revoke.isError) && <p className="error">{describeError(create.error ?? revoke.error)}</p>}
    </section>
  );
}

/** Once, to the owner, when the club reaches 10 members. It changes nothing on its own. */
function BigClubNudge({ clubId }: { clubId: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const dismiss = useMutation({ mutationFn: () => api.dismissNudge(clubId), onSuccess: () => qc.invalidateQueries({ queryKey: ["club", clubId] }) });
  return (
    <section className="vote-head nudge">
      <div className="grow">
        <b>{txt.yourClubJustReached}</b>
        <div className="d">{txt.aBusyShelfIs}</div>
        <div className="row" style={{ gap: 8, flexWrap: "wrap", marginTop: 10 }}>
          <button type="button" className="btn sm" disabled={dismiss.isPending} onClick={() => { dismiss.mutate(); void navigate({ to: "/clubs/$clubId/club/settings", params: { clubId } }); }}>{txt.reviewShelfSettings}</button>
          <button type="button" className="btn ghost sm" disabled={dismiss.isPending} onClick={() => dismiss.mutate()}>{txt.notNow}</button>
        </div>
      </div>
    </section>
  );
}
